import react, { useEffect, useState } from "react";
import uRLs from "../../../utils/baseUtils";
import CurrencyConverter, { toCurrency } from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import InterRegular from "../sale/font/Inter_24pt-Regular-normal";
import InterBold from "../sale/font/Inter_24pt-Bold-normal";
import { CircularProgress } from "@mui/material";
import handleError from "../../../constants/handleError";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import { useContextApi } from "../../../context/ContextApi";
import { CloseIcon, DownloadIcon, PrinterIcon } from "../../../constants/Icons";

const ViewVoucher = ({ onClose, viewData }) => {
  const { settings } = useAuth();
  const showAlert = useShowAlert();
  const [download, setDownload] = useState(false);
  const [print, setPrint] = useState(false);
  const { checkNetwork } = useNetwork();
  const {useEscapeKey}=useContextApi();
  const handleClose = () => {
    onClose();
  };
  useEscapeKey(handleClose);
  const fetchTrackMode = (trackMode) => {
    switch (trackMode?.toUpperCase()) {
      case "JOBCARD":
        return "Jobcard";
      case "PAYLATER":
        return "Paylater";
      default:
        return trackMode;
    }
  };

  const formatTrackMode = (voucherMode) => {
    switch (voucherMode?.toUpperCase()) {
      case "RECEIPT_VOUCHER":
        return "RECEIPT VOUCHER";
      case "PAYMENT_VOUCHER":
        return "PAYMENT VOUCHER";
      case "CONTRA_VOUCHER":
        return "CONTRA VOUCHER";
      default:
        return voucherMode;
    }
  };

  const paymentMode = (mode) => {
    switch (mode.toUpperCase()) {
      case "ONLINE":
        return "Online";
      case "CASH":
        return "Cash";
      case "CARD":
        return "Card";
      case "UPI":
        return "UPI";
      default:
        return mode;
    }
  };

  const saleDate = viewData?.dateOfIssue;
  const timezone = "Asia/kolkata";
  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  const loadImageDimensions = (base64Src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = base64Src;
    });
  };

  const generateinvoicePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const padding = 7;
    const headerX = 2;
    const headerY = 2;
    const imagePath =
      settings.imagePrefix + "profile/" + settings?.saleProfileData?.image;

    if (settings?.saleProfileData?.image) {
      try {
        // Convert image URL to base64
        const response = await fetch(imagePath);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        // Determine format
        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(imagePath);

        // Add image to PDF
        doc.addImage(
          base64,
          format,
          headerX + padding,
          headerY + padding,
          imgWidth,
          imgHeight
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    const invoiceFontSize = 20;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(invoiceFontSize);
    // Estimate vertical alignment by adjusting for font size
    const textHeightOffset = invoiceFontSize * 0.55;
    const invoiceTextY = headerY + textHeightOffset;
    const invoiceX = pageWidth - padding;
    const invoiceText =
      viewData?.voucherMode && formatTrackMode(viewData?.voucherMode);
    const invoiceTextWidth = doc.getTextWidth(invoiceText);
    doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
    // If cancelled, show "CANCELLED" immediately to the right of "INVOICE"
    if (viewData.status === "CANCELLED") {
      const cancelledFontSize = invoiceFontSize * 0.6;
      doc.setFontSize(cancelledFontSize);
      doc.setTextColor(220, 53, 69); // Bootstrap danger red
      doc.setFont("Inter-Bold", "bold");
      // Position CANCELLED right after INVOICE
      const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
      doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
      // Reset styles
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(invoiceFontSize);
    }
    // Prepare font size for labels and values
    doc.setFontSize(10);
    const invoiceNoLabel = "Voucher No:";
    const invoiceNoValue = viewData.voucherNumber;
    const invoiceDateLabel = "Date:";
    const invoiceDateValue = DateConverter.ISOToDisplayFormat(
      viewData.dateOfIssue
    ).date;
    // Measure total widths
    const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
    const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
    const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
    const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
    const gap = 10;
    const innerGap = 2;
    const invoiceRowY = 25;
    const totalTextWidth =
      invoiceNoLabelWidth +
      innerGap +
      invoiceNoValueWidth +
      gap +
      invoiceDateLabelWidth +
      innerGap +
      invoiceDateValueWidth;
    const invoiceRowX = pageWidth - padding - totalTextWidth;
    const lineStartX = padding;
    const lineEndX = invoiceRowX - 5;
    const lineY = invoiceRowY;
    // Middle line
    doc.setDrawColor("#e2e4e6");
    doc.setLineWidth(2);
    doc.setLineCap("round");
    doc.line(lineStartX, lineY, lineEndX, lineY);

    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceNoValue,
      invoiceRowX + invoiceNoLabelWidth + innerGap,
      invoiceRowY
    );
    const dateX =
      invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;

    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceDateLabel, dateX, invoiceRowY);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceDateValue,
      dateX + invoiceDateLabelWidth + innerGap,
      invoiceRowY
    );
    const sectionTop = 35;
    const midX = pageWidth / 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.text("Store Info:", padding, sectionTop);
    doc.setFontSize(12);
    const hasSaleToData =
      viewData?.customerInfo &&
      (viewData?.customerInfo?.name ||
        viewData?.customerInfo?.phone );

    if (hasSaleToData) {
      doc.setFontSize(12);
      doc.text("Voucher To:", midX, sectionTop);
    }
    doc.setFontSize(10);
    let fromY = sectionTop + 5;
    let toY = sectionTop + 5;
    const fromDetails = [
      { bold: false, text: `Title: ${viewData?.storeInfo?.title || "-"}` },
      { bold: false, text: `Email: ${viewData?.storeInfo?.email || ""}` },
    ];
    
    const sectionGap = 12;
    const sectionWidth = (pageWidth - padding * 2 - sectionGap) / 2 + 20;

    let toDetails = [];
    if (hasSaleToData) {
      toDetails = [
        { bold: true, text: viewData?.customerInfo?.name || "-" },
        {
          bold: false,
          text: [
            viewData?.customerInfo?.addressline1,
            viewData?.customerInfo?.addressline2,
            viewData?.customerInfo?.district,
            viewData?.customerInfo?.country,
            viewData?.customerInfo?.pincode ? `- ${viewData?.customerInfo?.pincode}` : "-",
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...(viewData?.customerInfo?.phone
          ? [{ bold: false, text: `Phone: ${viewData?.customerInfo?.phone? viewData?.customerInfo?.phone:"-"}` }]
          : []),
        ...(viewData?.customerInfo?.email
          ? [{ bold: false, text: `Email: ${viewData?.customerInfo?.email?viewData?.customerInfo?.email:"-"}` }]
          : []),
        ...(viewData?.customerInfo?.gstnumber
          ? [
              {
                bold: true,
                text: `GST:`,
                suffix: ` ${viewData?.customerInfo?.gstnumber}`,
              },
            ]
          : []),
        ...(viewData?.customerInfo?.state
          ? [
              {
                bold: true,
                text: `State:`,
                suffix: ` ${viewData?.customerInfo?.state} [ State Code: ${
                  viewData?.customerInfo?.statecode || ""
                } ]`,
              },
            ]
          : []),
        ...(viewData?.customerInfo?.referenceid
          ? [
              {
                bold: true,
                text: "Reference No:",
                suffix: ` ${viewData?.customerInfo?.referenceid}`,
              },
            ]
          : []),
      ];
    }
    const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
    const lineHeight = 5;

    const renderDetails = (details, startX, startY) => {
      let y = startY;
      details.forEach(({ bold, text, suffix = "" }) => {
        const fullText = text + suffix;
        const lines = wrapText(fullText, sectionWidth);
        lines.forEach((line, index) => {
          if (index === 0 && suffix && bold) {
            const labelWidth = doc.getTextWidth(text);
            doc.setFont("Inter-Bold", "bold");
            doc.text(text, startX, y);
            doc.setFont("Inter-Regular", "normal");
            doc.text(suffix, startX + labelWidth, y);
          } else {
            doc.setFont(
              bold ? "Inter-Bold" : "Inter-Regular",
              bold ? "bold" : "normal"
            );
            doc.text(line, startX, y);
          }
          y += lineHeight;
        });
      });

      return y;
    };
    fromY = renderDetails(fromDetails, padding, fromY);
    if (hasSaleToData) {
      toY = renderDetails(toDetails, midX, toY);
    }
    // 3. Product Table
    const productItems = Array.isArray(viewData)
  ? viewData
  : [viewData];

const productBody = productItems?.map((item, i) => {
  const paymentmode = item?.paymentMode ? paymentMode(item?.paymentMode) : ""
  const mode = item?.trackMode ? fetchTrackMode(item?.trackMode):item?.trackMode;
  const debit = item?.amountMode === "DEBIT" && `₹${CurrencyConverter.toCurrency(item?.amount || 0,"INR")}`
  const credit = item?.amountMode === "CREDIT" && `₹${CurrencyConverter.toCurrency(item?.amount || 0,"INR")}`

  const desc = [
    `${item?.title} ${item?.taxInfo?.taxPercent !== 0 ? `- ${item?.taxInfo?.taxPercent}%` : ""}`,
    ...(item?.description ? [item?.description] : []),
  ].join("\n");

  return [
    i + 1,
    desc,
    mode,
    paymentmode,
    debit,
    credit,
  ];
});

    // Add total row INSIDE the same table
    autoTable(doc, {
      startY: Math.max(fromY, toY) + 3,
      head: [
        [
          "S.No",
          "Description",
          "Mode",
          "payment Mode",
          "Debit",
          "Credit",
        ],
      ],
      body: productBody,
      margin: { left: 6, right: 5 }, // add right margin if needed
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 9,
        halign: "middle",
        cellPadding: 2,
        lineColor: [200, 200, 200],
        lineWidth: 0.2,
        textColor: "#041121",
      },
      headStyles: {
        fillColor: "#f4f4f6",
        textColor: "#292d51",
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 13, halign: "center", valign: "middle" },
        1: { cellWidth: 90, halign: "left" },
        2: { cellWidth: 21, halign: "center", valign: "middle" },
        3: { cellWidth: 30, halign: "center", valign: "middle" },
        4: { cellWidth: 21, halign: "center", valign: "middle" },
        5: { cellWidth: 21, halign: "center", valign: "middle" },
      },
      willDrawCell: function (data) {
        if (data.column.index === 1 && data.section === "body") {
          const itemIndex = data.row.index;
          const item = data.saleitems?.[itemIndex];
          if (!item) return;

          const x = data.cell.x + 2;
          let y = data.cell.y + 4;
          const lineHeight = 4;
          const currency = CurrencyConverter;

          const lines = [
            { type: "title", text: item?.title },
            { type: "description", text: item?.description }
          ];
          data.cell.text = [];
          lines.forEach((line) => {
            if (line.type === "title") {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(15);
              doc.setTextColor(0, 0, 0);
              doc.text(line.text, x, y);
              doc.setFontSize(9);
            } else {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(6);
              doc.setTextColor(0, 0, 0);
              doc.text(line.label, x, y);
              doc.setFont("Inter-Regular", "normal");
              doc.setTextColor(80, 80, 80);
              doc.text(line.value, x + 32, y);
            }
            y += lineHeight;
          });
        }
      },

      theme: "grid",
    });
    const body = [
      [
        {
          content: "Total Amount In Words",
          styles: {
            fontStyle: "bold",
            font: "Inter-Bold",
            fontSize: 9,
            valign: "top",
          },
        },
        {
          content: `${viewData?.amountInWords}`,
          styles: { halign: "right", fontSize: 9 },
        },
      ],
    ];

    const afterProductsY = doc.lastAutoTable?.finalY + 0.3;
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20;
    const estimatedSummaryHeight = 50;
    let summaryStartY;
    let isSamePage = true;
    if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
      summaryStartY = afterProductsY;
    } else {
      doc.addPage();
      summaryStartY = 20;
      isSamePage = false;
    }
    autoTable(doc, {
      startY: summaryStartY,
      body: body,
      pageBreak: "avoid",
      margin: { left: 6, right: 5 },
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        halign: "middle",
        cellPadding: 2,
        fontSize: 9,
        lineColor: [200, 200, 200],
        lineWidth: 0.2,
        textColor: "#041121",
      },
      columnStyles: {
        0: { cellWidth: 103,halign: "center" },
        1: { cellWidth: 93, halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && isSamePage) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        const lastRowIndex = body.length - 1;
        if (data.section === "body") {
          if (data.row.index === lastRowIndex) {
            data.cell.styles.fillColor = "#f4f4f6";
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.font = "Inter-Bold";
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.font = "Inter-Regular";
            data.cell.styles.fontStyle = "normal";
          }
        }
      },
      theme: "grid",
    });

    const afterTablesY=doc.lastAutoTable?.finalY + 3
    const taxinfoBody = [
      ["Amount", `₹${CurrencyConverter.toCurrency(viewData?.amount || 0,'INR')}`],
      ["Tax Amount", `₹${CurrencyConverter.toCurrency(viewData?.taxInfo?.taxAmount||0,'INR')}`],
      ...(
        viewData?.taxMode === "CGST_SGST"
          ? [
      ["SGST Amount", `₹${CurrencyConverter.toCurrency(viewData?.taxInfo?.sgstAmount || 0,'INR')}`],
      ["CGST Amount", `₹${CurrencyConverter.toCurrency(viewData?.taxInfo?.cgstPercent || 0,"INR")}`]]:[]),
      ...(
        viewData?.taxMode === "IGST"
          ? [
              ["IGST Amount", `₹${CurrencyConverter.toCurrency(viewData?.taxInfo?.igstAmount || 0, "INR")}`],
            ]
          : []
      ),
      
    ];
    
    const taxinfoHead = [
      [
        {
          content: "Tax Info",
          colSpan: 2, 
          styles: {
            halign: "center",
            fillColor: "#f4f4f6",
            textColor: "#292d51",
            fontStyle: "bold",
            font: "Inter-Bold",
          },
        },
      ],
    ];
    autoTable(doc, {
      startY: afterTablesY,
      head: taxinfoHead,
      body: taxinfoBody,
      margin: { left: 6, right: 5 },
      tableWidth: doc.internal.pageSize.getWidth() - 15,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 9,
        halign: "left",
        cellPadding: 2,
        lineColor: [200, 200, 200],
        lineWidth: 0.2,
        textColor: "#041121",
      },
      
      columnStyles: {
        0: { cellWidth: 30, fontStyle: "bold" }, // Label column
        1: { cellWidth: 45, halign: "right" },   // Value column
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && isSamePage) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        if (data.section === "body") {
            data.cell.styles.font = "Inter-Regular";
            data.cell.styles.fontStyle = "normal";
          }
      },
      theme: "grid",
    });

    const afterSummaryY = doc.lastAutoTable.finalY;
    const pageHeight2 = doc.internal.pageSize.getHeight();
    const marginBottom2 = 20;
    const declarationHeight = 45;
    let finalY;
    if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
      doc.addPage();
      finalY = 10;
    } else {
      finalY = afterSummaryY;
    }
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    const footerY = finalY + 15;
    doc.setFontSize(10);
    doc.setFont("Inter-Regular", "normal");
    doc.text("***** Thank You! Visit Us Again *****", pageWidth / 2, footerY, {
      align: "center",
    });
    // const boxTopY = footerY + 10;
    // const boxHeight = 25;
    // const boxLeftX = padding;
    // const boxRightX = pageWidth - padding;
    // const boxWidth = boxRightX - boxLeftX;
    // const boxMidX = boxLeftX + boxWidth / 2;

    // doc.setFontSize(10);
    // doc.setDrawColor(200, 200, 200);
    // doc.setLineWidth(0.2);
    // doc.rect(boxLeftX, boxTopY, boxWidth, boxHeight);
    // doc.line(boxMidX, boxTopY, boxMidX, boxTopY + boxHeight);

    // const startY = boxTopY + 6;
    // const lineHeight2 = 5;
    // const leftContentWidth = boxMidX - boxLeftX - 10;
    // const rightContentWidth = boxRightX - boxMidX - 10;

    // // Left side: Declaration label (bold)
    // doc.setFont("Inter-Bold", "bold");
    // doc.text("Declaration:", boxLeftX + 5, startY);

    // // Left side: Declaration text (normal)
    // let declarationText =
    //   "We declare that this tax invoice shows the actual price of the goods described and that all particulars are true and correct.";
    // doc.setFont("Inter-Regular", "normal");
    // const leftLines = doc.splitTextToSize(declarationText, leftContentWidth);
    // leftLines.forEach((line, i) => {
    //   doc.text(line, boxLeftX + 5, startY + (i + 1) * lineHeight2);
    // });

    // // Right side: Small note (right aligned)
    // doc.setFont("Inter-Regular", "normal");
    // let rightText = "* Computer Generated Document No sign Required";
    // const rightLines = doc.splitTextToSize(rightText, rightContentWidth);
    // rightLines.forEach((line, i) => {
    //   doc.text(line, boxRightX - 5, startY + i * lineHeight2, {
    //     align: "right",
    //   });
    // });
    setDownload(false);
    setPrint(false);
    return doc;
  };

  
  const handleDownload = async (data) => {
    setDownload(true);
    const { voucherMode, voucherNumber } = data || {};

    let doc;
    
      doc = await generateinvoicePDF();

    // const filenamePrefix = formatTrackMode(voucherMode)?.replace(/\s+/g, "_");
    // doc.save(`${filenamePrefix}_${voucherNumber}.pdf`);
    doc.save(`Voucher.pdf`);
  };
  
  const handlePrint = async () => {
    setPrint(true);
    try {
      if (!checkNetwork()) return;
      let doc;
          doc = await generateinvoicePDF();
      const blob = doc.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 500);
      };
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPrint(false);
    }
  };

  useEffect(() => {
    jsPDF.API.events.push([
      "addFonts",
      function () {
        this.addFileToVFS("Inter_24pt-Regular-normal.ttf", InterRegular);
        this.addFileToVFS("Inter_24pt-Bold-normal.ttf", InterBold);
        this.addFont(
          "Inter_24pt-Regular-normal.ttf",
          "Inter-Regular",
          "normal"
        );
        this.addFont("Inter_24pt-Bold-normal.ttf", "Inter-Bold", "bold");
      },
    ]);
  }, []);

  return (
    <>
      <div>
        <div className="modal-backdrop show"></div>
        <div
          className="modal show"
          id="CustomerModal"
          tabIndex="-1"
          aria-modal="true"
          role="dialog"
          style={{ display: "block" }}
        >
          <div className="modal-dialog modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <div className="d-flex align-items-center">
                  <h6 className="modal-title">Voucher</h6>
                </div>
                <div className="d-flex align-items-center  gap-2">
                  <p className="small mb-0 ">
                    <b className="tm_primary_color"> Created On:</b>{" "}
                    {saleTime.date} {""}
                    {saleTime.time}
                  </p>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
              </div>
              <div className="modal-body" id="element-to-download">
                <div className="tm_container">
                  <div className="tm_invoice_wrap">
                    <div className="tm_invoice tm_style1 tm_type1">
                      <div className="tm_invoice_in">
                        <table width="100%" className="pdfcontent">
                          <tbody>
                            <tr>
                              <td width="50%" style={{ textAlign: "left" }}>
                                <div className="tm_logo">
                                  {settings?.saleProfileData?.image && (
                                    <img
                                      src={
                                        settings.imagePrefix +
                                        "profile/" +
                                        settings?.saleProfileData?.image
                                      }
                                      className="img-rounded logo mb-0"
                                      height="50"
                                    />
                                  )}
                                </div>
                              </td>
                              <td width="50%" style={{ textAlign: "right" }}>
                                <div className="tm_primary_color fs-4 tm_text_uppercase">
                                  {viewData?.voucherMode &&
                                    formatTrackMode(viewData?.voucherMode)}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div className="tm_invoice_info tm_mb20">
                          <div className="tm_invoice_seperator tm_gray_bg"></div>
                          <div className="tm_invoice_info_list">
                            <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                              <b className="tm_primary_color">Voucher No: </b>{" "}
                              {viewData.voucherNumber}
                            </p>
                            <p className="tm_invoice_date tm_m0 mt-3">
                              <b className="tm_primary_color"> Date: </b>{" "}
                              {saleTime.date}
                            </p>
                          </div>
                        </div>
                        <table width="100%" className="pdfcontent">
                          <tbody>
                            <tr>
                              <td width="35%" style={{ verticalAlign: "top" }}>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    lineHeight: "22px",
                                    fontWeight: "600",
                                    marginBottom: "0px",
                                  }}
                                >
                                  Store Info:
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "400",
                                    marginBottom: "0px",
                                  }}
                                >
                                  Title :{viewData.storeInfo?.title || ""}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "400",
                                    marginBottom: "0px",
                                  }}
                                >
                                  Email: {viewData.storeInfo?.email || ""}
                                </div>
                              </td>

                              <td width="10%"></td>
                              <td width="55%" style={{ verticalAlign: "top" }}>
                                {viewData.customerInfo &&
                                  (viewData.customerInfo?.name ||
                                    viewData.customerInfo?.addressline1 ||
                                    viewData.customerInfo?.addressline2 ||
                                    viewData.customerInfo?.state ||
                                    viewData.customerInfo?.district ||
                                    viewData.customerInfo?.country ||
                                    viewData.customerInfo?.pincode ||
                                    viewData.customerInfo?.phone ||
                                    viewData.customerInfo?.email ||
                                    viewData.customerInfo?.gstnumber ||
                                    viewData.customerInfo?.statecode ||
                                    viewData.customerInfo?.referid) && (
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "14px",
                                          lineHeight: "22px",
                                          fontWeight: "600",
                                          marginBottom: "0px",
                                        }}
                                      >
                                        Voucher To:
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          marginBottom: "0px",
                                        }}
                                      >
                                        {viewData.customerInfo?.name || ""}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          lineHeight: "18px",
                                          marginBottom: "0px",
                                        }}
                                      >
                                        <div>
                                          {viewData.customerInfo
                                            ?.addressline1 || ""}{" "}
                                          {viewData.customerInfo
                                            ?.addressline2 || ""}{" "}
                                          {viewData.customerInfo?.state || ""}{" "}
                                          {viewData.customerInfo?.district ||
                                            ""}{" "}
                                          {viewData.customerInfo?.country || ""}{" "}
                                          -{" "}
                                          {viewData.customerInfo?.pincode || ""}
                                        </div>
                                        {viewData.customerInfo?.phone && (
                                          <div>
                                            Phone: +91{" "}
                                            {viewData.customerInfo?.phone}
                                          </div>
                                        )}
                                        {viewData.customerInfo?.email && (
                                          <div>
                                            Email:{" "}
                                            {viewData.customerInfo?.email}
                                          </div>
                                        )}
                                        {viewData.customerInfo?.gstnumber && (
                                          <div>
                                            <strong>GST:</strong>{" "}
                                            {viewData.customerInfo?.gstnumber}
                                          </div>
                                        )}
                                        {viewData.customerInfo?.state && (
                                          <div>
                                            <strong>State:</strong>{" "}
                                            {viewData.customerInfo?.state} [
                                            State Code :
                                            {viewData.customerInfo?.statecode ||
                                              ""}{" "}
                                            ]
                                          </div>
                                        )}
                                        {viewData.customerInfo?.referid && (
                                          <div>
                                            <strong>Reference ID:</strong>{" "}
                                            {viewData.customerInfo?.referid}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <table
                          className="items"
                          width="100%"
                          style={{
                            marginTop: "6px",
                            borderCollapse: "collapse",
                          }}
                          cellPadding="4"
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  width: "20px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                S.No
                              </th>
                              {/* <th
                                style={{
                                  width: "80px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Voucher No
                              </th> */}
                              <th
                                style={{
                                  width: "260px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Description
                              </th>
                              <th
                                style={{
                                  width: "50px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Mode
                              </th>
                              <th
                                style={{
                                  width: "50px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Payment Mode
                              </th>
                              <th
                                style={{
                                  width: "50px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Debit
                              </th>
                              <th
                                style={{
                                  width: "50px",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Credit
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  textAlign: "center",
                                  fontSize: "12px",
                                }}
                              >
                                1
                              </td>
                              {/* <td
                                style={{
                                  textAlign: "center",
                                  fontSize: "12px",
                                }}
                              >
                                {viewData?.voucherNumber}
                              </td> */}
                              <td
                                style={{
                                  textAlign: "left",
                                  fontSize: "12px",
                                  width: "40%",
                                }}
                              >
                                <div>
                                  {viewData?.title}{" "}
                                  {viewData?.taxInfo?.taxPercent !== 0 && (
                                    <>
                                      - &nbsp;
                                      <small className="badge rounded-pill bg-label-peace">
                                        {viewData?.taxInfo?.taxPercent}%
                                      </small>
                                    </>
                                  )}
                                </div>
                                <div className="ms-0 fsz-10 lh-13 mb-1">
                                  {viewData.description}
                                </div>
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  fontSize: "12px",
                                }}
                              >
                                {viewData?.trackMode
                                  ? fetchTrackMode(viewData?.trackMode)
                                  : viewData?.trackMode}
                              </td>
                              <td className="text-center">
                                {viewData?.paymentMode
                                  ? paymentMode(viewData?.paymentMode)
                                  : ""}
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  fontSize: "12px",
                                }}
                              >
                                {viewData.amountMode === "DEBIT" &&
                                  `₹${CurrencyConverter.toCurrency(
                                    viewData.amount || 0,
                                    "INR"
                                  )}`}
                              </td>
                              <td
                                style={{
                                  textAlign: "center",
                                  fontSize: "12px",
                                }}
                              >
                                {viewData.amountMode === "CREDIT" &&
                                  `₹${CurrencyConverter.toCurrency(
                                    viewData.amount || 0,
                                    "INR"
                                  )}`}
                              </td>
                            </tr>

                            <tr>
                              <td
                                style={{
                                  textAlign: "center",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                }}
                                colSpan="2"
                              >
                                Total Amount In Words
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                                colSpan="4"
                              >
                                {viewData.amountInWords}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div className="d-flex">
                          <table
                            className="items"
                            width="38%"
                            style={{
                              marginTop: "6px",
                              // borderCollapse: "collapse",
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr>
                                <th
                                  colSpan={2}
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    background: "#f3f3f3",
                                    border: "0.5mm solid #e0e0e0",
                                  }}
                                >
                                  Tax Info
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="">Amount</td>
                                <td
                                  width={60}
                                  style={{
                                    textAlign: "end",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                  }}
                                >
                                  ₹
                                  {CurrencyConverter.toCurrency(
                                    viewData?.taxInfo?.amount || 0,
                                    "INR"
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td className="">Tax Amount</td>
                                <td
                                  width={60}
                                  style={{
                                    textAlign: "end",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                  }}
                                >
                                  ₹
                                  {CurrencyConverter.toCurrency(
                                    viewData?.taxInfo?.taxAmount || 0,
                                    "INR"
                                  )}
                                </td>
                              </tr>
                              {viewData?.taxMode === "IGST" && (
                                <tr>
                                  <td className="">IGST Amount</td>
                                  <td
                                    width={60}
                                    style={{
                                      textAlign: "end",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                    }}
                                  >
                                    ₹
                                    {CurrencyConverter.toCurrency(
                                      viewData?.taxInfo?.igstAmount || 0,
                                      "INR"
                                    )}
                                  </td>
                                </tr>
                              )}
                              {viewData?.taxMode === "CGST_SGST" && (
                                <>
                                  <tr>
                                    <td className="">SGST Amount</td>
                                    <td
                                      width={60}
                                      style={{
                                        textAlign: "end",
                                        fontSize: "12px",
                                        fontWeight: 400,
                                      }}
                                    >
                                      ₹
                                      {CurrencyConverter.toCurrency(
                                        viewData?.taxInfo?.sgstAmount || 0,
                                        "INR"
                                      )}
                                    </td>
                                  </tr>

                                  <tr>
                                    <td className="">CGST Amount</td>
                                    <td
                                      width={60}
                                      style={{
                                        textAlign: "end",
                                        fontSize: "12px",
                                        fontWeight: 400,
                                      }}
                                    >
                                      ₹
                                      {CurrencyConverter.toCurrency(
                                        viewData?.taxInfo?.cgstAmount || 0,
                                        "INR"
                                      )}
                                    </td>
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                          {/* <table
                          className="items ms-3"
                          width="38%"
                          style={{
                            marginTop: "6px",
                          }}
                          cellPadding="4"
                        >
                          <thead>
                            <tr>
                              <th
                                colSpan={2}
                                style={{
                                  width: "100%",
                                  textAlign: "center",
                                  background: "#f3f3f3",
                                  border: "0.5mm solid #e0e0e0",
                                }}
                              >
                                Payment Mode
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="">Cash</td>
                              <td
                                width={60}
                                style={{
                                  textAlign: "left",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                }}
                              >{viewData?.paymentMode==="CASH" ? (
                                
                                `₹${CurrencyConverter.toCurrency(viewData?.amount||0,'INR')}`):0}</td>
                            </tr>
                            <tr>
                              <td className="">Card</td>
                              <td
                                width={60}
                                style={{
                                  textAlign: "left",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                }}
                              >{viewData?.paymentMode==="CARD" ? (
                                
                                `₹${CurrencyConverter.toCurrency(viewData?.amount||0,'INR')}`):0}</td>
                            </tr>
                          
                            <tr>
                              <td className="">Online</td>
                              <td
                                width={60}
                                style={{
                                  textAlign: "left",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                }}
                              >{viewData?.paymentMode==="ONLINE" ? (
                                
                                `₹${CurrencyConverter.toCurrency(viewData?.amount||0,'INR')}`):0}</td>
                            </tr>
                          
                             <tr>
                              <td className="">UPI</td>
                              <td
                                width={60}
                                style={{
                                  textAlign: "left",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                }}
                              >{viewData?.paymentMode==="UPI" ? (
                                
                                `₹${CurrencyConverter.toCurrency(viewData?.amount||0,'INR')}`):0}</td>
                            </tr>
                          </tbody>
                        </table> */}
                        </div>
                        <div className="d-flex justify-content-center mt-3"><span>***** Thank You! Visit Us Again *****</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer d-block">
                <button
                  onClick={handleClose}
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-outline-secondary text-left"
                >
                  <CloseIcon iClass={"fs-15"}/> Close
                </button>
                <div className="btn-group text-right align-items-center">
                  {print ? (
                      <button className="btn bg-label-animal btn-sm pb-0" disabled>
                        <CircularProgress size={18} className="text-black" />
                      </button>
                    ) : (
                  <>
                    <button
                        onClick={() => handlePrint()}
                      className="btn bg-label-animal shadow-none btn-sm rounded-end"
                      id="printreceiptbtn"
                    >
                      <PrinterIcon iClass={"fs-15"}/> Print{" "}
                    </button>
                  </>
                   )} 
                </div>
                
                  {download ? (
                    <button
                      className="btn btn-warning btn-sm pb-0 text-right"
                      disabled
                    >
                      <CircularProgress size={18} className="text-black" />
                    </button>
                  ) : (
                <button
                  className="btn btn-warning btn-sm text-right"
                  id="generatebillpdfbtn"
                    onClick={() => handleDownload(viewData)}
                >
                  <DownloadIcon iClass={"fs-15"}/>
                </button>
                )}
                {/* {shareLoad ? (
                    <button
                      className="btn btn-success btn-sm pb-0 text-right"
                      disabled
                    >
                      <CircularProgress size={18} className="text-black" />
                    </button>
                  ) : ( */}
                {/* <button
                  className="btn btn-success btn-sm text-right"
                  id="generatebillpdfbtn"
                  //   onClick={() => handleWhappShare(data)}
                >
                  <i className="bx bxl-whatsapp fs-20"></i>
                </button> */}
                {/* )} */}
                {/* {data.status !== "CANCELLED" &&
                    (edit ? (
                      <button
                        className="btn btn-warning pb-0 text-right"
                        disabled
                      >
                        <CircularProgress size={18} className="text-black" />
                      </button>
                    ) : ( */}
                {/* <button
                  className="btn btn-label-primary pb-0 text-right"
                  // onClick={() => handleEdit(data)}
                >
                  <i className="ng ng-ng-edit-icon fs-15"></i>
                </button> */}
                {/* ))} */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewVoucher;
